/**
 * SimulationPage — Composer 시뮬레이션(작성→최종 보고서 dry-run 검증) 전체 화면 뷰.
 *
 * 라우트: /report-composer/simulation?templateKey=<key> — Layout(사이드바) 없는 전용 라우트
 * (preview-popout 동형). 에디터에서 새 탭으로 열며, **저장본 템플릿을 서버에서 다시 로드**하므로
 * 에디터의 미저장 편집 상태는 반영되지 않는다(진입 게이트가 isDirty를 막는 것과 이중 보장).
 *
 * 흐름 3단계(결정 기록 docs/decisions/2026-07-26-composer-simulation.md):
 *  (a) 컨텍스트 선택 — PreviewContextBar(실데이터 적용과 동일 admin 캐스케이드) 재사용
 *  (b) 작성 화면 — rc 승격 조립(ComposerWidgetTreeRenderer + COMPOSED_FORM_WIDGETS)으로 web
 *      배포판(new-composed.tsx)과 동일 화면. 프리필/AI 후보/평가방식 재resolve도 동형.
 *      작성 폼 렌더러는 하나이고 트리의 출처만 둘이다(저작 `composerWidgetTree` / 본문 바인딩
 *      파생 `deriveWriteForm`) — `writeTree` memo가 그 선택을 소유한다(2026-09-30 S3-b).
 *      영속 부수효과 콜백(저장·카탈로그 등록 writeback)은 no-op/로컬 합성 + 안내.
 *  (c) 최종 보고서 — POST admin/report-composer/simulate-compose(dry-run, 부수효과 0) 결과를
 *      상세와 동일한 렌더 경로(AdminComposedDocumentBody = resolveForRender + PagedComposerView +
 *      결재란 special)로 표시. 실보고서는 생성되지 않는다.
 *
 * ⚠️ web new-composed.tsx가 정본(ground-truth) — 초기값/프리필/후보/재resolve 규칙이 바뀌면
 * 여기(및 simulationLogic.ts)도 같이 고칠 것.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  applyVariableDynamicOptions,
  collectMissingRequired,
  collectSortableSelectOptionSources,
  ComposePreviewView,
  ComposeWritePageShell,
  ComposerWidgetTreeRenderer,
  COMPOSED_FORM_WIDGETS,
  COMPOSED_ROW_WIDGETS,
  collectCatalogParamVariableKeys,
  focusMissingRequiredField,
  methodTriggerSignature,
  mergeMethodReresolve,
  missingRequiredToastMessage,
  resolveFindingRiskMethodValue,
  ReportWriteShell,
  useActiveComposerView,
} from '@safetyhub/report-composer';
import type { ComposeChromeButtonProps } from '@safetyhub/report-composer';
import type {
  CatalogEntry,
  ComposeChromeItem,
  ComposeChromeSlot,
  ComposerWidget,
  ImprovementCandidatePart,
  InputWidget,
  PreviewSimulationAxis,
  PreviewSimulationOption,
  ReportComposerPreviewContext,
  ReportTemplate,
} from '@safetyhub/shared';
import {
  COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS,
  deriveTemplateApprovalPolicy,
  improvementCandidatesToRows,
  PREVIEW_SIMULATION_SPLIT_AXIS_KEY,
} from '@safetyhub/shared';
import { Button, Card, ConfirmModal } from '@safetyhub/ui';
import { adminApi, extractApiError } from '../../lib/api';
import type { SimulateComposeResult } from '../../lib/api';
import { useToastStore } from '../../stores/toast';
import { AdminComposedDocumentBody } from '../../components/reports/AdminComposedDocumentBody';
import { PreviewContextBar, type PreviewContextLabels } from './PreviewContextBar';
import { derivePreviewSimulationAxes } from './previewSimulationAxes';
import { createSimulationSuggestSearch } from './simulationSuggestions';
import {
  buildInitialWriteValues,
  buildLocalCatalogRow,
  buildSimulationApprovalData,
  buildSimulationTitle,
  collectPrefillListRows,
  deriveFormNodes,
  readLastSimContext,
  writeLastSimContext,
} from './simulationLogic';
import { resolveSimulationDetailChrome } from './simulationDetailChrome';

/** 상단 고정 배너 높이(px) — 셸/미리보기의 sticky top 오프셋으로 전달해 배너 아래에 붙게 한다. */
const SIM_BANNER_HEIGHT = 34;

/**
 * 상단 고정 배너 — 모든 단계 공통. note는 단계별 보조 안내(결과 화면의 "PDF와 동일" 문구 —
 * 기존 시뮬 전용 헤더에서 이관해 배포 chrome 배치를 오염하지 않게 한다, 2026-07-26).
 */
function SimBanner({ note }: { note?: string }) {
  return (
    <div
      className="sticky top-0 z-[60] flex items-center justify-center bg-amber-500 text-xs font-semibold text-white"
      style={{ height: SIM_BANNER_HEIGHT }}
    >
      시뮬레이션 — 실제 보고서가 생성되지 않습니다
      {note && <span className="ml-2 font-normal opacity-90">· {note}</span>}
    </div>
  );
}

/** 현재 뷰포트 폭(px) — 하단바 fit 기반 오버플로우 판정용(web useComposedReportActions 동형). */
function useViewportWidth(): number {
  const [width, setWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1280,
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

export default function SimulationPage() {
  const [searchParams] = useSearchParams();
  const templateKey = searchParams.get('templateKey') ?? '';
  const showToast = useToastStore((s) => s.showToast);

  // 저장본 템플릿 — 에디터 편집 상태와 독립 로드.
  const [template, setTemplate] = useState<ReportTemplate | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [step, setStep] = useState<'context' | 'write' | 'result'>('context');
  // 컨텍스트 초기값 = 마지막 사용값 복원(2026-07-26 사용자 확정 — 새로고침/재시작 시 재입력 생략).
  // 필드 화이트리스트만 취해 저장소 오염 방어. stale id(삭제된 점검 등)는 캐스케이드 옵션에 안
  // 떠서 재선택 유도 + 서버 dry-run 정합 검증이 최종 방어라 별도 실존 검증은 두지 않는다.
  const [context, setContext] = useState<ReportComposerPreviewContext>(() =>
    readLastSimContext(),
  );
  // 선택 엔티티 라벨 — 화면 표시용(작성 단계 헤더의 현장명).
  const [labels, setLabels] = useState<PreviewContextLabels>({});
  // 재마운트 복원용 모사 라벨 — **표시용 state와 일부러 분리한다.**
  //
  // PreviewContextBar는 단계 전환(context ↔ write)마다 언마운트되고, 돌아오면 새로 마운트된다.
  // 모사 값은 opaque라 라벨 없이는 이름을 잃으므로(PreviewContextLabels JSDoc §④) 마지막으로
  // 보고받은 라벨을 되돌려 줘야 한다.
  //
  // ⚠️ 그 되돌림을 `labels` state로 하면 "부모 state → 자식 prop → 자식 state(lazy init)" 순환이
  // 된다 — 지금은 lazy init이라 마운트에서만 읽혀 무해하지만, 그 한 줄이 바뀌는 순간 의미가
  // 조용히 달라진다. ref로 분리해 두면 "표시"와 "재마운트 복원"이 서로 다른 값이라는 게 드러난다
  // (RealDataModal은 커밋본 prop에서 seed하고 draft로 보고해 이미 둘을 나눠 두었다).
  const restoreSimLabelsRef = useRef<Record<string, string> | undefined>(undefined);
  const handleLabelsResolved = useCallback((next: PreviewContextLabels) => {
    restoreSimLabelsRef.current = next.simulation;
    setLabels(next);
  }, []);
  // 변경 즉시 저장 — "작성 시작" 전 새로고침해도 마지막 입력이 남게(확정 시점 저장보다 강건).
  useEffect(() => {
    writeLastSimContext(context);
  }, [context]);

  // ── 모사 축(결정 2026-09-01 「모사 축」 §①·②) ─────────────────────────────
  // 에디터와 **같은 축 목록**을 시뮬레이션에도 준다. 종전엔 여기에 분할 칸이 아예 없어
  // 시뮬레이션은 항상 통합이었고, "에디터는 공종별로 보는데 시뮬레이션은 통합"이라는 비대칭이
  // 있었다. 축 파생은 순수 함수 하나라 두 화면이 같은 선언에서 같은 목록을 받는다.
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await adminApi.getReportComposerCatalog();
        if (!cancelled && res.success && res.data) setCatalog(res.data);
      } catch {
        // 무시 — 모사 후보가 비게 되며, 컨텍스트 선택 자체는 그대로 동작한다.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const simulationAxes = useMemo(
    () => derivePreviewSimulationAxes(catalog, template ?? undefined),
    [catalog, template],
  );
  // `plan` 축 선택지 조회 — ⚠️ 부수효과 있음(§④). EditorPage의 같은 이름 콜백과 동형이며,
  // 호출 시점(축 추가 조작 1회)은 PreviewContextBar가 소유한다.
  const loadAxisOptions = useCallback(
    async (
      axis: PreviewSimulationAxis,
      ctx: ReportComposerPreviewContext,
    ): Promise<PreviewSimulationOption[]> => {
      if (axis.key !== PREVIEW_SIMULATION_SPLIT_AXIS_KEY || !template) {
        return axis.options ?? [];
      }
      const res = await adminApi.splitPlanForReportComposerByContext(template, ctx);
      if (!res.success || !res.data) {
        throw new Error(res.error?.message ?? '분할 그룹을 불러오지 못했습니다.');
      }
      return (res.data.plan?.groups ?? []).map((group) => ({
        label: `${group.label} (${group.rowIds.length}건)`,
        value: { groupKey: group.groupKey, rowIds: group.rowIds },
      }));
    },
    [template],
  );

  // ── 작성 단계 state (web new-composed 동형) ──
  const [nodes, setNodes] = useState<InputWidget[]>([]);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [resolvedRows, setResolvedRows] = useState<Record<string, Record<string, unknown>[]>>({});
  const [initialValuesReady, setInitialValuesReady] = useState(false);
  // 되돌리기 기준 스냅샷(초기값+prefill) — web initialSnapshotRef 동형(siteId는 컨텍스트 확정이라 값만).
  const initialSnapshotRef = useRef<Record<string, unknown> | null>(null);
  const candidatesResolvedRef = useRef(false);

  // 현장 fallback — 컨텍스트에서 현장 미선택 시 작성 화면에서 선택(web 직접 URL 진입 fallback 동형).
  const [sites, setSites] = useState<{ id: string; name: string }[]>([]);
  const [siteId, setSiteId] = useState('');

  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const previewFromScrollYRef = useRef(0);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const [simResult, setSimResult] = useState<SimulateComposeResult | null>(null);
  const [isComposing, setIsComposing] = useState(false);

  const activeView = useActiveComposerView();
  const viewportWidth = useViewportWidth();

  // 템플릿 로드 — 저장본만(에디터 미저장 변경 미반영 보장).
  useEffect(() => {
    if (!templateKey) {
      setLoadError('templateKey가 없습니다. 에디터에서 시뮬레이션을 열어주세요.');
      return;
    }
    let cancelled = false;
    adminApi
      .getReportTemplate(templateKey)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) setTemplate(res.data);
        else setLoadError('템플릿을 불러오지 못했습니다.');
      })
      .catch((err) => {
        if (!cancelled) setLoadError(extractApiError(err).message || '템플릿을 불러오지 못했습니다.');
      });
    return () => {
      cancelled = true;
    };
  }, [templateKey]);

  // 저작 트리에 변수 동적 옵션 축 반영 — web new-composed effectiveTree 동형.
  const effectiveTree = useMemo(
    () =>
      template?.composerWidgetTree?.length
        ? applyVariableDynamicOptions(template.composerWidgetTree, template.variables)
        : undefined,
    [template],
  );
  /**
   * 작성 폼의 **단일 정본 트리** — 렌더와 필수 검증이 같은 값을 읽는다(web new-composed 동형,
   * 2026-09-30 S3-b). 저작 트리가 있으면 그것, 없으면 본문 바인딩 파생 `nodes`(`InputWidget[]`).
   *
   * ⚠️ 종전 이 페이지의 검증 갈래는 `template.composerWidgetTree`(raw)를 읽어 렌더가 보는
   * `effectiveTree`(변수 동적 옵션 반영본)와 어긋나 있었다 — 이 memo 하나로 그 축도 닫힌다.
   * 파생 쪽에 `applyVariableDynamicOptions`를 걸지 않는 사유는 web new-composed의 같은 memo 참조.
   */
  const writeTree = useMemo<ComposerWidget[]>(() => effectiveTree ?? nodes, [effectiveTree, nodes]);

  // 점검 컨텍스트 — inspectionIds 우선, 단일 inspectionId는 1건 배열로(web query 수용 동형).
  const inspectionIds = useMemo(
    () =>
      context.inspectionIds?.length
        ? context.inspectionIds
        : context.inspectionId
          ? [context.inspectionId]
          : undefined,
    [context.inspectionIds, context.inspectionId],
  );

  // prefill resolve 컨텍스트 — web suggestContext 동형 + 작성자(userId) 시뮬레이션.
  const prefillContext = useMemo<ReportComposerPreviewContext>(
    () => ({
      companyId: context.companyId || undefined,
      siteId: context.siteId || undefined,
      inspectionIds,
      userId: context.userId || undefined,
    }),
    [context.companyId, context.siteId, inspectionIds, context.userId],
  );

  // 현장 fallback 목록 — 컨텍스트에 현장이 없을 때만 필요.
  useEffect(() => {
    if (step !== 'write' || context.siteId || !context.companyId) return;
    let cancelled = false;
    adminApi
      .getSites({ organizationId: context.companyId, limit: 200 })
      .then((res) => {
        if (!cancelled && res.success && res.data) {
          setSites(res.data.map((s) => ({ id: s.id, name: s.name })));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [step, context.siteId, context.companyId]);

  /** 작성 시작 — 초기값 seed + prefill resolve(web 템플릿 로드 effect의 생성 경로 동형). */
  const startWrite = useCallback(() => {
    if (!template || !context.companyId) return;
    const tpl = template;
    const formNodes = deriveFormNodes(tpl);
    setNodes(formNodes);
    const initial = buildInitialWriteValues(tpl, formNodes);
    setValues(initial);
    setResolvedRows({});
    candidatesResolvedRef.current = false;
    setInitialValuesReady(false);
    initialSnapshotRef.current = initial;
    setSiteId(context.siteId ?? '');
    setSimResult(null);
    setMode('edit');
    setStep('write');

    // N1: 편집 가능한 prefill — 서버 resolve(admin prefill-by-context = web getComposerPrefill과
    // 동일 서버 로직) 결과를 defaultValue 위에 덮는다. 실패는 graceful(무시).
    adminApi
      .prefillReportComposerByContext(tpl, prefillContext)
      .then((res) => {
        const prefill = res.success && res.data ? res.data : {};
        if (Object.keys(prefill).length === 0) return;
        setValues((prev) => ({ ...prev, ...prefill }));
        // 카탈로그 list 후보 소스는 resolvedRows 채널에도 합류(모달 스코프 도달 — web 동형).
        // AI 후보(별도 effect)가 정본이 되도록 prev를 뒤에 병합.
        const listRows = collectPrefillListRows(tpl, prefill);
        if (Object.keys(listRows).length > 0) {
          setResolvedRows((prev) => ({ ...listRows, ...prev }));
        }
        initialSnapshotRef.current = { ...initial, ...prefill };
      })
      .catch(() => {
        // prefill 실패(네트워크/없음)는 무시 — defaultValue fallback으로 동작.
      })
      .finally(() => setInitialValuesReady(true));
  }, [template, context.companyId, context.siteId, prefillContext]);

  // AI 개선 의견 후보 — 템플릿에 파트 소스 sortableSelect가 있고 점검 컨텍스트가 있으면 resolve
  // (web new-composed 동형 — admin 경로는 improvement-candidates-by-context, 같은 서버 생성기).
  // 진입 전 사용자 입력(web 타입 선택 모달 sessionStorage)은 시뮬 진입엔 없음 — 무지시(web 직접
  // URL 진입과 동형, admin DTO에 guidance 필드 없음).
  useEffect(() => {
    if (step !== 'write' || candidatesResolvedRef.current || !template) return;
    const tree = template.composerWidgetTree;
    if (!tree?.length) return;
    const sourceKeys = collectSortableSelectOptionSources(tree);
    const detectedParts = (
      Object.keys(COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS) as ImprovementCandidatePart[]
    ).filter((p) => sourceKeys.includes(COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS[p]));
    if (detectedParts.length === 0) return;
    const companyId = context.companyId;
    if (!companyId || !inspectionIds?.length) return;
    candidatesResolvedRef.current = true;
    adminApi
      .improvementCandidatesForReportComposer(inspectionIds[0], companyId, detectedParts)
      .then((res) => {
        if (!res.success || !res.data) return;
        const result = res.data;
        setResolvedRows((prev) => {
          const next = { ...prev };
          for (const p of detectedParts) {
            const rows = improvementCandidatesToRows(result, [p]);
            if (rows.length > 0) next[COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS[p]] = rows;
          }
          return next;
        });
      })
      .catch(() => {
        // 후보 생성 실패는 무시 — 후보 미주입 = 버튼 숨김(web 동형).
      });
  }, [step, template, context.companyId, inspectionIds]);

  // 평가방식(findingRiskMethod) 변경 재resolve — web new-composed P4e 동형.
  // 트리거는 카탈로그 파라미터 지정이 참조하는 변수 — rc 단일 지점 판정.
  const hasMethodTrigger = useMemo(
    () => collectCatalogParamVariableKeys(template).size > 0,
    [template],
  );
  const methodTriggerSig = useMemo(
    () => methodTriggerSignature(template, values),
    [template, values],
  );
  const reresolvedMethodRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (initialValuesReady) reresolvedMethodRef.current = methodTriggerSig;
    // baseline 확정 순간의 값만 필요 — 이후 변경 감지는 아래 effect가 담당(web 동형).
    // (admin eslint에는 react-hooks 플러그인이 없어 disable 주석 불요 — 미정의 규칙 참조가 error가 된다.)
  }, [initialValuesReady]);
  useEffect(() => {
    if (step !== 'write' || !initialValuesReady || !template || !hasMethodTrigger) return;
    if (!context.companyId) return;
    if (methodTriggerSig === reresolvedMethodRef.current) return;
    const tpl = template;
    // 서버에 실어 보낼 방식 값 — 서버 resolveFindingRiskMethod와 같은 규칙(지정이 축을 소유).
    const methodStr = resolveFindingRiskMethodValue(tpl, values);
    let cancelled = false;
    const timer = setTimeout(() => {
      adminApi
        .prefillReportComposerByContext(tpl, { ...prefillContext, findingRiskMethod: methodStr })
        .then((res) => {
          const fresh = res.success && res.data ? res.data : {};
          if (cancelled || Object.keys(fresh).length === 0) return;
          reresolvedMethodRef.current = methodTriggerSig;
          setValues((prev) => mergeMethodReresolve(prev, fresh, tpl));
          const listRows = collectPrefillListRows(tpl, fresh);
          if (Object.keys(listRows).length > 0) {
            setResolvedRows((prev) => ({ ...prev, ...listRows }));
          }
        })
        .catch(() => {
          // 재조회 실패는 비차단 — 기존 값 유지(web 동형).
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // 트리거 시그니처 변경만 트리거(web 동형). prefillContext는 안정 참조(useMemo).
  }, [methodTriggerSig, initialValuesReady, hasMethodTrigger, step]);

  const handleChange = useCallback((bindingKey: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [bindingKey]: value }));
  }, []);

  // 카탈로그 행 즉시 등록 — **로컬 합성만**(서버 write 없음 = 부수효과 0). web은
  // submitComposerCatalogRow(영속 keyed upsert)를 호출하지만 시뮬은 저장하지 않는다.
  // 반환 행을 resolvedRows에 합류시켜 콤보 제안·연결 경로는 배포판과 동일하게 동작.
  const handleCatalogRowSubmit = useCallback(
    async (
      catalogKey: string,
      submitValues: Record<string, string>,
    ): Promise<Record<string, unknown> | null> => {
      if (!siteId) return null;
      const row = buildLocalCatalogRow(
        submitValues,
        `sim-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      );
      setResolvedRows((prev) => {
        const cur = prev[catalogKey] ?? [];
        return { ...prev, [catalogKey]: [...cur, row] };
      });
      showToast('시뮬레이션에서는 실제로 등록되지 않습니다.', 'info');
      return row;
    },
    [siteId, showToast],
  );

  // 검색 제안(suggestionsFrom) — 저작자가 시뮬에서 자기 설정을 확인할 수 있어야 하므로 배포
  // 작성 화면과 같은 데이터를 admin 경로로 조회한다. **선택 통지(usage 증가)는 배선하지 않음**
  // (시뮬 = 부수효과 0 — handleCatalogRowSubmit이 로컬 합성만 하는 것과 같은 원칙).
  const handleSuggestSearch = useMemo(
    () => createSimulationSuggestSearch(context.companyId || undefined),
    [context.companyId],
  );

  // 사진 업로드(photo 위젯/칼럼) — 시뮬 = 부수효과 0 원칙 그대로, 서버 저장 없이 로컬
  // 미리보기 URL(createObjectURL)로 흐름만 검증한다. URL은 시뮬 세션 수명 동안만 유효 —
  // createObjectURL은 revoke 전까지 File 전체를 메모리에 붙들므로 발급분을 모아 언마운트에서
  // 일괄 revoke한다(cleanup은 ref를 읽는다 — 발급마다 effect를 다시 걸지 않기 위해).
  const photoObjectUrlsRef = useRef<string[]>([]);
  const simulatePhotoUpload = useCallback(async (file: File) => {
    const url = URL.createObjectURL(file);
    photoObjectUrlsRef.current.push(url);
    return url;
  }, []);
  useEffect(
    () => () => {
      for (const url of photoObjectUrlsRef.current) URL.revokeObjectURL(url);
      photoObjectUrlsRef.current = [];
    },
    [],
  );

  const siteName =
    sites.find((s) => s.id === siteId)?.name || (context.siteId ? (labels.site ?? '') : '');

  /** "최종 보고서 보기" — 필수 검증(web 제출 동형) 후 dry-run 합성 호출. */
  const handleShowFinal = useCallback(async () => {
    if (!template || !context.companyId) return;
    // 점검 앵커가 있으면 현장 필수(서버 정합 검증 조합 그대로).
    if (inspectionIds?.length && !siteId) {
      showToast('점검을 앵커하려면 현장을 선택해주세요.', 'error');
      return;
    }
    // 필수(required) 미입력 차단 — web 제출과 동일 검증. 렌더와 **같은 트리**(writeTree)를 본다.
    // ⚠️ 이 인자를 `template.composerWidgetTree`(raw)로 되돌려도 **화면 동작이 같다**(노드 수·
    // required·bindingKey가 같고 변수 동적 옵션만 갈린다 / 파생 경로는 늘 `[]`) — 그래서 이 축의
    // 탐지기는 `SimulationPage.writeTreeWiring.rtl.test.tsx`의 **인자 단언**뿐이다.
    const missing = collectMissingRequired(writeTree, values, activeView);
    if (missing.length > 0) {
      // 안내 문구·스크롤/포커스는 rc 공용(missingRequiredToastMessage/focusMissingRequiredField)
      // — 토스트 호출만 host 소유. 반복 행 안 위젯은 rc 헬퍼가 접두 매칭으로 첫 행 인스턴스를 잡는다.
      showToast(missingRequiredToastMessage(missing), 'error');
      focusMissingRequiredField(missing[0].id);
      return;
    }
    setIsComposing(true);
    try {
      const res = await adminApi.simulateReportComposerCompose({
        templateKey: template.key,
        companyId: context.companyId,
        siteId: siteId || undefined,
        inspectionIds,
        // 제목은 web 제출 규칙 동형 — 현장 미선택이면 서버 fallback(템플릿 name).
        title: siteId ? buildSimulationTitle(template.name, siteName) : undefined,
        content: values,
        userId: context.userId || undefined,
      });
      if (res.success && res.data) {
        setSimResult(res.data);
        setStep('result');
        window.scrollTo(0, 0);
      } else {
        showToast('시뮬레이션 합성에 실패했습니다.', 'error');
      }
    } catch (err) {
      showToast(extractApiError(err).message || '시뮬레이션 합성에 실패했습니다.', 'error');
    } finally {
      setIsComposing(false);
    }
  }, [template, context.companyId, context.userId, inspectionIds, siteId, siteName, values, writeTree, activeView, showToast]);

  // 되돌리기 — 초기 스냅샷(초기값+prefill) 복원(web 동형).
  const handleReset = () => {
    const snap = initialSnapshotRef.current;
    if (snap) setValues(snap);
    setResetConfirmOpen(false);
  };

  // chrome 버튼 동작 매핑 — web new-composed getButtonProps 동형. 라벨·배치도 배포판 그대로
  // (2026-07-26 사용자 확정 — 시뮬 전용 라벨 override 제거). 차이(의도적)는 동작만:
  // submit=dry-run 합성, cancel=컨텍스트 단계 복귀, link=이동 안내만.
  const getButtonProps = (
    item: ComposeChromeItem,
    _slot: ComposeChromeSlot,
  ): ComposeChromeButtonProps => {
    if (item.actionId === 'submit') {
      return {
        onClick: () => void handleShowFinal(),
        disabled: isComposing,
        // 진행 중 라벨도 web(create 모드 '작성 중...')과 동형.
        labelOverride: isComposing ? '작성 중...' : undefined,
      };
    }
    if (item.actionId === 'preview') {
      return {
        onClick: () => {
          previewFromScrollYRef.current = window.scrollY;
          setMode('preview');
        },
        disabled: !template,
      };
    }
    if (item.actionId === 'reset') {
      return { onClick: () => setResetConfirmOpen(true) };
    }
    if (item.actionId === 'cancel') {
      return { onClick: () => setStep('context') };
    }
    if (item.link) {
      // 커스텀 링크 버튼은 web 라우트 대상 — 시뮬(admin) 문맥에서는 이동하지 않는다.
      return { onClick: () => showToast('시뮬레이션에서는 이동하지 않습니다.', 'info') };
    }
    return { onClick: () => {} };
  };

  // ── 로드 실패/로딩 ──
  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-100">
        <SimBanner />
        <div className="mx-auto mt-16 max-w-md rounded-card-lg bg-white p-6 text-center shadow">
          <p className="text-sm text-red-600">{loadError}</p>
        </div>
      </div>
    );
  }
  if (!template) {
    return (
      <div className="min-h-screen bg-gray-100">
        <SimBanner />
        <div className="flex items-center justify-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-600" />
        </div>
      </div>
    );
  }

  // ── (b') 작성 중 미리보기 — web과 동일한 rc ComposePreviewView(전체화면) ──
  if (step === 'write' && mode === 'preview') {
    return (
      <div className="min-h-screen bg-gray-100">
        <SimBanner />
        <ComposePreviewView
          template={template}
          values={values}
          onBack={() => setMode('edit')}
          restoreScrollY={previewFromScrollYRef.current}
          stickyTopOffset={SIM_BANNER_HEIGHT}
        />
      </div>
    );
  }

  // ── (c) 최종 보고서 — 상세와 동일 렌더 경로(dry-run 결과 스냅샷 기반) + 배포 상세 chrome ──
  if (step === 'result' && simResult) {
    // 인쇄 미리보기 없음(2026-07-26 사용자 확정) — 실제 PDF는 Puppeteer가 이 결과 뷰와
    // 같은 페이지드 렌더 화면을 캡처하므로, 이 화면 자체가 PDF 결과물이다. window.print는
    // 브라우저 인쇄 CSS라는 별도 렌더 경로를 타서 배포판과 갈라져 제거.
    //
    // chrome은 web ComposerReportDetail의 DRAFT 직후와 동일 배치·라벨(2026-07-26 사용자 확정) —
    // 공유 표시 계층(ReportWriteShell + resolveDetailChromeActions)으로 그리고 동작만 no-op 토스트.
    // 뒤로가기(보고서 목록)만 시뮬 동선(작성 화면 복귀)으로 매핑.
    const snap =
      simResult.templateSnapshot && typeof simResult.templateSnapshot === 'object'
        ? (simResult.templateSnapshot as Partial<ReportTemplate>)
        : undefined;
    const detailChrome = snap?.detailChrome ?? template.detailChrome;
    const approvalLineMeta = snap?.approvalLineMeta ?? template.approvalLineMeta;
    const resolved = resolveSimulationDetailChrome({
      detailChrome,
      // 정책 유무는 템플릿 단독 권위(서버 resolveComposedApprovalMeta 동형) — shared 단일 파생(단계 존재).
      hasApprovalLinePolicy: deriveTemplateApprovalPolicy(approvalLineMeta).hasApprovalLinePolicy,
      approvalLineCount: simResult.approvalLineDefinition?.length ?? 0,
      hasInspectionContext: !!inspectionIds?.length,
      availableWidth: viewportWidth,
      onAction: () => showToast('시뮬레이션에서는 동작하지 않습니다.', 'info'),
    });
    return (
      <div className="min-h-screen bg-gray-100">
        <SimBanner note="이 화면이 PDF와 동일한 결과입니다" />
        <ReportWriteShell
          onBack={() => setStep('write')}
          backLabel="보고서 목록"
          collapseHeaderActionsOnMobile
          headerActions={resolved.headerRight}
          headerLeftActions={resolved.headerLeft.length > 0 ? resolved.headerLeft : undefined}
          footerActions={resolved.actionBar}
          stickyTopOffset={SIM_BANNER_HEIGHT}
        >
          {/* 콘텐츠 구조 — web ComposerReportDetail 동형(제목 + 상태 배지 + 현장명 + 본문). */}
          <div className="max-w-4xl xl:max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
                {simResult.title}
              </h1>
              <div className="mt-1.5 sm:mt-2 flex flex-wrap items-center gap-1.5 sm:gap-2">
                {/* DRAFT 배지 — web lib/reports getReportStatusLabel/Color('DRAFT')('초안',
                    gray) 동형(admin은 web lib 접근 불가라 DRAFT 값만 고정 인용). */}
                <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  초안
                </span>
                {siteName && <span className="text-xs sm:text-sm text-gray-500">{siteName}</span>}
              </div>
            </div>
            <AdminComposedDocumentBody
              templateSnapshot={simResult.templateSnapshot}
              content={simResult.content}
              liveData={simResult.liveData}
              approvalData={buildSimulationApprovalData(simResult.approvalLineDefinition)}
            />
          </div>
        </ReportWriteShell>
      </div>
    );
  }

  // ── (a) 컨텍스트 선택 ──
  if (step === 'context') {
    return (
      <div className="min-h-screen bg-gray-100">
        <SimBanner />
        <div className="mx-auto mt-10 w-full max-w-md rounded-card-lg bg-white p-6 shadow">
          <h1 className="text-base font-bold text-gray-800">시뮬레이션 — {template.name}</h1>
          <p className="mt-2 mb-4 text-sm text-gray-500">
            실데이터 컨텍스트를 선택하면 그 데이터로 배포판과 동일한 작성 화면이 열립니다. 조직은
            필수, 현장·점검(회차)·계정(작성자)은 선택입니다.
          </p>
          <PreviewContextBar
            value={context}
            onChange={setContext}
            onLabelsResolved={handleLabelsResolved}
            vertical
            simulationAxes={simulationAxes}
            onLoadAxisOptions={loadAxisOptions}
            // 마운트 시 1회만 읽히는 seed(자식 lazy init). 표시용 `labels`가 아니라 복원 전용
            // ref에서 준다 — 위 선언부 주석 참조.
            initialSimulationLabels={restoreSimLabelsRef.current}
          />
          <p className="mt-3 text-xs text-gray-400">
            계정을 선택하면 그 작성자 기준으로 결재라인이 파생됩니다(미선택 시 결재라인 없이 합성).
          </p>
          <div className="mt-4 flex justify-end">
            <Button variant="primary" onClick={startWrite} disabled={!context.companyId}>
              작성 시작
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── (b) 작성 화면 — web new-composed와 동일 조립(rc 승격 모듈) ──
  return (
    <div className="min-h-screen bg-gray-50">
      <SimBanner />
      <ComposeWritePageShell
        config={template.composeChrome}
        activeView={activeView}
        mode="create"
        title={`${(template.name || '보고서').trim()} 작성`}
        subtitle={siteName || undefined}
        getButtonProps={getButtonProps}
        onBack={() => setStep('context')}
        backLabel="컨텍스트 선택"
        stickyChrome
        stickyTopOffset={SIM_BANNER_HEIGHT}
        className="min-h-full w-full"
      >
        <div className="space-y-5">
          {inspectionIds && inspectionIds.length > 0 && (
            <p className="rounded-card-sm bg-brand-50 px-2.5 py-1.5 text-xs text-brand-600">
              점검 {inspectionIds.length}건 기반으로 생성됩니다.
            </p>
          )}
          {/* 현장 fallback — 컨텍스트에서 현장 미선택 시(web의 !siteIdParam 분기 동형). */}
          {!context.siteId && (
            <Card elevation="none">
              <label htmlFor="sim-site" className="mb-1 block text-xs font-medium text-gray-600">
                현장
              </label>
              <select
                id="sim-site"
                className="w-full rounded-control border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
              >
                <option value="">-- 현장 선택 --</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </Card>
          )}

          {/* 렌더 게이트 — 종전 `nodes.length || dataSources || composerWidgetTree`와 등가(web 동형):
              `writeTree`가 저작 트리이거나 파생 `nodes`이므로 앞 두 항이 그 하나로 합쳐진다. */}
          {(writeTree.length > 0 || (template.dataSources?.length ?? 0) > 0) && (
            <div>
              {/* 작성 폼 렌더러는 하나다(2026-09-30 S3-b) — 트리의 출처만 둘(저작/파생, writeTree memo). */}
              <ComposerWidgetTreeRenderer
                tree={writeTree}
                values={values}
                onChange={handleChange}
                registeredWidgets={COMPOSED_ROW_WIDGETS}
                registeredFormWidgets={COMPOSED_FORM_WIDGETS}
                registeredWidgetContext={{ companyId: context.companyId ?? '', siteId }}
                dataSources={template.dataSources}
                dataDefs={template.dataDefs}
                resolvedRows={resolvedRows}
                onCatalogRowSubmit={siteId ? handleCatalogRowSubmit : undefined}
                onSuggestSearch={handleSuggestSearch}
                // 시뮬레이션은 저장 없는 검증이라 서버 업로드 대신 로컬 미리보기 URL을 쓴다
                // (admin 계정은 조직 멤버십이 없어 files/upload도 403 — 실제 저장은 web 작성 화면의 몫).
                onPhotoUpload={simulatePhotoUpload}
                initialValuesReady={initialValuesReady}
              />
            </div>
          )}
        </div>
      </ComposeWritePageShell>

      {/* 되돌리기 확인 — web 동형(danger=취소가 default 포커스). */}
      <ConfirmModal
        open={resetConfirmOpen}
        title="되돌리기"
        body="입력한 내용을 처음 상태로 되돌립니다."
        confirmLabel="되돌리기"
        cancelLabel="취소"
        danger
        onConfirm={handleReset}
        onClose={() => setResetConfirmOpen(false)}
      />
    </div>
  );
}
